import { EShowItemCategory } from '@pkg/lib-data'

/**
 * Mapping between Hebrew show category names and URL-friendly slugs
 */
export const SHOW_TYPE_SLUG_MAP: Record<EShowItemCategory, string> = {
  [EShowItemCategory.KIDS]: 'kids',
  [EShowItemCategory.MUSIC]: 'music',
  [EShowItemCategory.DANCE]: 'dance',
  [EShowItemCategory.STANDUP]: 'standup',
  [EShowItemCategory.THEATER]: 'theater',
  [EShowItemCategory.EXHIBITIONS]: 'exhibitions',
  [EShowItemCategory.SPORT]: 'sport',
  [EShowItemCategory.MOVIE]: 'movies',
  [EShowItemCategory.LECTURES]: 'lectures',
  [EShowItemCategory.PARTIES]: 'parties',
}

/**
 * Reverse mapping from slug to category
 */
export const SLUG_TO_CATEGORY_MAP: Record<string, EShowItemCategory> = Object.entries(SHOW_TYPE_SLUG_MAP).reduce(
  (acc, [category, slug]) => {
    acc[slug] = category as EShowItemCategory

    return acc
  },
  {} as Record<string, EShowItemCategory>,
)

/**
 * Get URL slug from show category enum value
 */
export const getSlugFromCategory = (category: EShowItemCategory): string => {
  return SHOW_TYPE_SLUG_MAP[category] || ''
}

/**
 * Get show category enum value from URL slug
 */
export const getCategoryFromSlug = (slug: string): EShowItemCategory | null => {
  return SLUG_TO_CATEGORY_MAP[slug] || null
}

/**
 * Extract category slug from URL path
 * Example: /contents/shows/central/music -> music (any /shows/{region}/…)
 */
export const extractCategorySlugFromPath = (pathname: string): string | null => {
  // Match: /contents/shows/{region}/{categorySlug} or /shows/{region}/{categorySlug}
  const match = pathname.match(/\/(?:contents\/)?shows\/[a-z-]+\/([^/]+)$/)

  return match && match[1] ? match[1] : null
}

/**
 * Get category from URL path or context
 */
export const getCategoryFromPathOrContext = (pathname: string, contextType?: string): string | null => {
  // First, try to get from context (data-type attribute)
  if (contextType) {
    return contextType
  }

  // Otherwise, extract from path
  const slug = extractCategorySlugFromPath(pathname)

  if (!slug) {
    return null
  }

  // Convert slug to Hebrew category name
  const category = getCategoryFromSlug(slug)

  return category || null
}

/**
 * Build path-based URL for category
 */
export const buildCategoryUrl = (basePath: string, categorySlug: string | null): string => {
  // Get all valid category slugs to check if last segment is a category
  const validSlugs = Object.values(SHOW_TYPE_SLUG_MAP)

  // Normalize basePath - remove any existing category slug if it matches the pattern
  // Pattern: /contents/shows/central/{slug} or /shows/central/{slug} -> /contents/shows/central or /shows/central
  let normalizedBase = basePath

  // Extract the last segment
  const lastSegment = basePath.split('/').filter(Boolean).pop() || ''

  // Check if the last segment is a category slug
  const isCategorySlug = validSlugs.includes(lastSegment)

  // If it's a category slug, remove it to get the base path
  if (isCategorySlug) {
    normalizedBase = basePath.replace(/\/[^/]+$/, '') || basePath
  }

  if (!categorySlug) {
    // Return base path without category (e.g., /contents/shows/central)
    return normalizedBase
  }

  const result = `${normalizedBase}/${categorySlug}`

  return result
}
